import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Checkbox, message, Tooltip } from 'antd';
import useMutation from '../../hooks/useMutation';
import { moveFeedCard as moveFeedCardStore } from '../../actions';
import { MoveFeedCardInput, MoveFeedCardMutation } from '../../graphql/api';
import { moveFeedCard } from '../../graphql/mutations';
import useUserPreference from '../../hooks/useUserPreference';

type IWordItem = {
  text?: string;
  isHighlighted: boolean;
  onClick: () => void;
  cardId?: string;
  recordingId?: string;
  startTime?: string | null;
  isActive?: boolean | undefined;
  disabled?: boolean;
  editingTranscript: boolean;
  isEditingWord: boolean;
};

const WordItem: React.FC<IWordItem> = ({
  text,
  isHighlighted,
  onClick,
  cardId,
  recordingId,
  startTime,
  isActive,
  disabled,
  editingTranscript,
  isEditingWord,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const dispatch = useDispatch();
  const { preference, updatePreference } = useUserPreference(
    'dontShowFeedcardWordTooltip'
  );
  const [tooltipHided, setTooltipHided] = React.useState<boolean>(!!preference);

  useEffect(() => {
    if (preference) setTooltipHided(true);
  }, [preference]);

  const moveFeedCardMutation = useMutation<
    MoveFeedCardInput,
    MoveFeedCardMutation
  >(moveFeedCard);

  const onDragStart = useCallback(
    (e: React.DragEvent, cardId?: string, startTime?: string | null) => {
      if (isHighlighted && cardId && startTime) {
        const ghostEle = document.createElement('div');
        ghostEle.setAttribute('id', `ghost-${cardId}`);
        ghostEle.classList.add('wordItemGhost');
        document.body.appendChild(ghostEle);

        e.dataTransfer.setData('cardID', cardId);
        e.dataTransfer.setData('originalTime', startTime);
        e.dataTransfer.setDragImage(ghostEle, 0, 0);
      }
    },
    [isHighlighted]
  );

  const onDragEnd = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const ghostEle = document.getElementById(`ghost-${cardId}`);
      if (ghostEle) {
        document.body.removeChild(ghostEle);
      }
    },
    [cardId]
  );

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!isHighlighted) {
        setIsHovered(true);
      }
    },
    [isHighlighted]
  );

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!isHighlighted) {
        setIsHovered(false);
      }
    },
    [isHighlighted]
  );

  const onDrop = useCallback(
    async (
      e: React.DragEvent,
      recordingID?: string,
      startTime?: string | null
    ) => {
      e.preventDefault();
      setIsHovered(false);

      const cardID = e.dataTransfer.getData('cardID');
      const originalTime = parseFloat(e.dataTransfer.getData('originalTime'));

      if (isHighlighted || !recordingID || !cardID || !startTime) {
        return;
      }

      const time = parseFloat(startTime);

      dispatch(moveFeedCardStore(recordingID, cardID, time));

      try {
        const result = await moveFeedCardMutation.callMutation({
          feedCardId: cardID,
          time: time,
        });
        if (!result.data.moveFeedCard?.success) {
          dispatch(moveFeedCardStore(recordingID, cardID, originalTime));
          message.error(
            result.data.moveFeedCard?.error ||
              'Error occurred while moving feed card.'
          );
        }
      } catch (err) {
        dispatch(moveFeedCardStore(recordingID, cardID, originalTime));
        message.error('Error occurred while moving feed card.');
      }
    },
    [dispatch, isHighlighted, moveFeedCardMutation]
  );

  const handleDontShowAgain = useCallback(() => {
    updatePreference(true);
    setTooltipHided(true);
  }, [updatePreference]);

  return (
    <Tooltip
      overlayClassName="hoveringInfoTooltip hoveringFeedcardTooltip"
      title={
        isHighlighted && !tooltipHided ? (
          <div>
            Click & drag to new
            <br />
            word to move
            <div className="dontShowAgainCheckboxTooltip">
              <Checkbox
                onChange={handleDontShowAgain}
                className="hoveringInfoTooltipCheckbox"
              />
              Don’t show again
            </div>
          </div>
        ) : (
          ''
        )
      }
    >
      <span
        className={`
          ${isHighlighted ? 'mark' : ''} 
          ${isHovered ? 'markDraggedOver' : ''} 
          ${isActive ? 'activeWordInTranscribe' : ''}
          ${disabled ? 'noPointerEvents' : ''}
          ${editingTranscript ? 'editingTranscript' : ''}
          ${isEditingWord ? 'isEditingWord' : ''}
        `}
        onClick={onClick}
        draggable={isHighlighted}
        onDragStart={e => onDragStart(e, cardId, startTime)}
        onDragEnd={e => onDragEnd(e)}
        onDragEnter={e => onDragEnter(e)}
        onDragOver={e => e.preventDefault()}
        onDragLeave={e => onDragLeave(e)}
        onDrop={e => onDrop(e, recordingId, startTime)}
      >
        {text}
      </span>
    </Tooltip>
  );
};

export default WordItem;
